/*
 * (C) Behaviour Interactive Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium, is strictly prohibited
 * This file is proprietary and confidential
 */

import { useCallback, useEffect, useRef } from 'react';

type ClickOutsideParams = {
  callback: () => void;
  listenGlobally?: boolean;
  parent?: HTMLElement | null;
};

export function useClickOutside({ callback, listenGlobally, parent }: ClickOutsideParams) {
  const ref = useRef<HTMLDivElement>(null);
  const refCallback = useRef<ClickOutsideParams['callback']>(callback);

  useEffect(() => {
    refCallback.current = callback;
  }, [callback]);

  const handler = useCallback((event: MouseEvent | TouchEvent | KeyboardEvent): void => {
    const element = ref.current;

    if (event instanceof KeyboardEvent) {
      if (event.key === 'Escape') {
        refCallback.current();
      }
    } else if (element && !element.contains(event.target as Node)) {
      refCallback.current();
    }
  }, []);

  useEffect(() => {
    const parentElement = listenGlobally ? document.body : parent || ref.current?.parentElement;

    if (parentElement) {
      parentElement.addEventListener('mousedown', handler);
      parentElement.addEventListener('touchstart', handler);
      parentElement.addEventListener('keydown', handler);

      return () => {
        parentElement.removeEventListener('mousedown', handler);
        parentElement.removeEventListener('touchstart', handler);
        parentElement.removeEventListener('keydown', handler);
      };
    }
  }, [handler]);

  return ref;
}
